import React from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";

import routes from "../../clhbid-routes";

import formatCurrency from "../helpers/format-currency";
import {
  BidType,
  DetailTypeEnum,
  getBidText,
  getBidType,
} from "../helpers/sale";

import Button from "../components/Button";
import SectionTitle from "../components/SectionTitle";

interface BaseSaleParcel {
  saleParcelType: DetailTypeEnum;
}
type SaleParcelParcel = BaseSaleParcel &
  Queries.SaleParcelsSaleFragment["parcels"][number] & {
    saleParcelType: DetailTypeEnum.Parcel;
  };
type SaleParcelEnBloc = BaseSaleParcel &
  Queries.SaleParcelsSaleFragment["parcels"][number] & {
    saleParcelType: DetailTypeEnum.EnBloc;
  };
type SaleParcelOption = BaseSaleParcel &
  Queries.SaleParcelsSaleFragment["options"][number] & {
    saleParcelType: DetailTypeEnum.Option;
  };
type SaleParcelFeature = BaseSaleParcel &
  Queries.SaleParcelsSaleFragment["parcels"][number]["features"][number] & {
    saleParcelType: DetailTypeEnum.Feature;
  };
type SaleParcel =
  | SaleParcelParcel
  | SaleParcelEnBloc
  | SaleParcelOption
  | SaleParcelFeature;

interface SaleParcelPresenter<T extends SaleParcel> {
  name: {
    label: string;
    value: (object: T) => string;
  };
  details: {
    label: string;
    value: (object: T) => string;
  };
  bid: {
    label: (sale: Queries.SaleParcelsSaleFragment) => string;
    value: (object: T, bidType: BidType) => string;
  };
  view: {
    label: string;
    route: (...args: unknown[]) => string;
  };
}

const parcelPresenter: SaleParcelPresenter<SaleParcelParcel> = {
  name: {
    label: "Parcel Name",
    value: (object) => object.name,
  },
  details: {
    label: "Parcel Size",
    value: (object) => object.size,
  },
  bid: {
    label: (sale) => getBidText(sale, true, false),
    value: (object, bidType) =>
      formatCurrency(object[bidType] || 0, "standard"),
  },
  view: {
    label: "View Details & Offer",
    route: routes.parcel.details,
  },
};
const enBlocPresenter: SaleParcelPresenter<SaleParcelEnBloc> = {
  name: {
    label: "En Bloc Name",
    value: (object) => object.name,
  },
  details: {
    label: "En Bloc Size",
    value: (object) => object.size,
  },
  bid: {
    label: (sale) => getBidText(sale, true, false),
    value: (object, bidType) =>
      formatCurrency(object[bidType] || 0, "standard"),
  },
  view: {
    label: "View En Bloc",
    route: routes.parcel.details,
  },
};
const optionPresenter: SaleParcelPresenter<SaleParcelOption> = {
  name: {
    label: "Option Name",
    value: (object) => object.name,
  },
  details: {
    label: "Option Details",
    value: (object) => object.category,
  },
  bid: {
    label: () => "Option Price",
    value: (object) => formatCurrency(object.price, "standard"),
  },
  view: {
    label: "View Option",
    route: routes.option.details,
  },
};
const featurePresenter: SaleParcelPresenter<SaleParcelFeature> = {
  name: {
    label: "Feature Name",
    value: (object) => object.name,
  },
  details: {
    label: "Feature Details",
    value: (object) => object.category,
  },
  bid: {
    label: () => "Included With",
    value: (object) => object.parcel[0].name,
  },
  view: {
    label: "View Feature",
    route: routes.feature.details,
  },
};

const objectPresenterMap: Record<
  DetailTypeEnum,
  SaleParcelPresenter<SaleParcel>
> = {
  parcel: parcelPresenter,
  enBloc: enBlocPresenter,
  option: optionPresenter,
  feature: featurePresenter,
};

function getObjectType(object: {
  __typename: string;
  enBloc?: boolean;
}): DetailTypeEnum {
  if (object.__typename === "ContentfulParcel")
    return object.enBloc ? DetailTypeEnum.EnBloc : DetailTypeEnum.Parcel;
  if (object.__typename === "ContentfulOption") return DetailTypeEnum.Option;
  if (object.__typename === "ContentfulFeature") return DetailTypeEnum.Feature;
}

const SaleParcels: React.FC<{
  sale: Queries.SaleParcelsSaleFragment;
}> = ({ sale }) => {
  const data = useStaticQuery<Queries.SaleParcelsQueryQuery>(query);

  const bidType = getBidType(sale);

  const objects: SaleParcel[] = [];

  for (const parcel of sale.parcels) {
    const saleParcelParcel: SaleParcelParcel | SaleParcelEnBloc = {
      saleParcelType: parcel.enBloc
        ? DetailTypeEnum.EnBloc
        : DetailTypeEnum.Parcel,
      ...parcel,
    };
    objects.push(saleParcelParcel);

    if (parcel.features) {
      for (const feature of parcel.features) {
        const saleParcelFeature: SaleParcelFeature = {
          saleParcelType: DetailTypeEnum.Feature,
          ...feature,
        };
        objects.push(saleParcelFeature);
      }
    }
  }

  if (sale.options) {
    for (const option of sale.options) {
      const saleParcelOption: SaleParcelOption = {
        saleParcelType: DetailTypeEnum.Option,
        ...option,
      };
      objects.push(saleParcelOption);
    }
  }

  return (
    <div id="parcels" className="mx-auto mb-12 max-w-[1200px] lg:px-4">
      <SectionTitle>Available Parcels</SectionTitle>
      <div className="gap-8 lg:flex lg:flex-row-reverse lg:items-stretch">
        <div className="mb-12 px-4 lg:mb-0 lg:h-[400px] lg:w-[480px] lg:px-0">
          <GatsbyImage
            alt={`Map of ${sale.name}`}
            image={
              (sale.saleMapImage || data.defaultImage.childImageSharp)
                .gatsbyImageData
            }
            className="block h-full w-full overflow-hidden rounded"
          />
        </div>
        <div className="relative bg-clhbid-gray lg:h-[400px] lg:flex-grow lg:overflow-x-visible lg:overflow-y-scroll lg:overscroll-none lg:rounded">
          <div className="px-2 lg:px-6 lg:py-2">
            <table className="relative block w-full text-white">
              <tbody className="block lg:grid lg:grid-cols-[auto,auto,auto,min-content]">
                {objects.map((object) => {
                  const objectType = getObjectType(object);

                  return (
                    <tr
                      key={object.slug}
                      className="grid grid-cols-[auto_auto_auto] grid-rows-2 gap-4 border-b border-clhbid-silver-dark py-4 lg:contents lg:py-0"
                    >
                      <td className="block w-full font-bold leading-none text-clhbid-orange lg:border-b lg:border-clhbid-silver-dark lg:py-4 lg:pr-4">
                        <span className="mb-[6px] block text-xs font-semibold leading-none text-clhbid-silver-light">
                          {objectPresenterMap[objectType].name.label}
                        </span>{" "}
                        <Link
                          className="transition-colors hover:text-clhbid-orange-dark"
                          to={objectPresenterMap[objectType].view.route(object)}
                          state={{ modal: true }}
                        >
                          {objectPresenterMap[objectType].name.value(object)}
                        </Link>
                      </td>
                      <td className="block font-bold leading-none lg:border-b lg:border-clhbid-silver-dark lg:py-4 lg:pr-4">
                        <span className="mb-[6px] block text-xs font-semibold leading-none text-clhbid-silver-light">
                          {objectPresenterMap[objectType].details.label}
                        </span>{" "}
                        {objectPresenterMap[objectType].details.value(object)}
                      </td>
                      <td className="block font-bold leading-none lg:border-b lg:border-clhbid-silver-dark lg:py-4 lg:pr-4">
                        <span className="mb-[6px] block text-xs font-semibold leading-none text-clhbid-silver-light">
                          {objectPresenterMap[objectType].bid.label(sale)}
                        </span>{" "}
                        {objectPresenterMap[objectType].bid.value(
                          object,
                          bidType
                        )}
                      </td>
                      <td className="col-span-3 flex items-start justify-end lg:col-span-1 lg:border-b lg:border-clhbid-silver-dark lg:py-4">
                        <Button
                          outline
                          size="small"
                          state={{ modal: true }}
                          href={objectPresenterMap[objectType].view.route(
                            object
                          )}
                          className="w-full lg:w-[124px]"
                        >
                          {objectPresenterMap[objectType].view.label}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const query = graphql`
  query SaleParcelsQuery {
    defaultImage: file(relativePath: { eq: "coming-soon.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
  }
`;

export const SaleParcelsFragment = graphql`
  fragment SaleParcelsSale on ContentfulSale {
    name
    saleStatus
    saleMapImage {
      gatsbyImageData
    }
    parcels {
      name
      slug
      size
      enBloc
      startTime
      acceptedBid
      startingBid
      features {
        name
        slug
        category
        parcel {
          name
          slug
          sale {
            slug
          }
        }
        __typename
      }
      sale {
        slug
      }
      __typename
    }
    options {
      name
      slug
      category
      exerciseDate
      price
      sale {
        slug
        saleStatus
      }
      __typename
    }
  }
`;

export default SaleParcels;
